import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Filter, Download, Search, UserCheck, UserX, Users } from 'lucide-react';
import { useAuth } from '@context/AuthContext';

import UserList from '@components/users/UserList';
import UserEntryModal from '@components/users/UserEntryModal';
import Button from '@components/common/Button';
import { userService } from '@services/userService';
import toast from 'react-hot-toast';

const UsersPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'Users - RDX Ticketing System';
  }, []);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Build export parameters
      const exportParams = {};
      if (searchQuery) exportParams.search = searchQuery;
      if (statusFilter !== 'all') exportParams.status = statusFilter;
      if (roleFilter !== 'all') exportParams.role = roleFilter;
      
      const response = await userService.exportUsers(exportParams, 'csv');
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Users exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(error.response?.data?.message || 'Failed to export users');
    } finally {
      setIsExporting(false);
    }
  };

  const handleAddUser = () => {
    setShowAddUserModal(true);
  };

  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.32,
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32 } }
  };

  // Inline style objects to match Dashboard visual language
  const styles = {
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    hero: {
      borderRadius: '1rem',
      padding: '1.25rem',
      color: 'white',
      overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(2,6,23,0.08)'
    },
    heroInner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem'
    },
    heroLeft: {
      flex: 1,
      minWidth: 0
    },
    heroTitle: {
      fontSize: '1.375rem',
      fontWeight: 700,
      margin: 0,
      color: 'white',
      lineHeight: 1.1
    },
    heroSub: {
      marginTop: '0.375rem',
      fontSize: '0.9375rem',
      color: 'rgba(255,255,255,0.9)'
    },
    heroRight: {
      textAlign: 'right',
      minWidth: '11rem'
    },
    dateText: {
      margin: 0,
      fontSize: '0.9375rem',
      color: 'rgba(255,255,255,0.9)'
    },
    metaText: {
      marginTop: '0.25rem',
      fontSize: '0.8125rem',
      color: 'rgba(255,255,255,0.85)',
      textTransform: 'capitalize'
    },
    cardWrapper: {
      backgroundColor: 'transparent'
    },
    actionButtons: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '0.5rem'
    }
  };

  // gradient background for users (green theme)
  const heroGradient = {
    background: 'linear-gradient(90deg, #10b981, #059669)'
  };

  return (
    <motion.div
      style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Users Hero */}
      <motion.div variants={itemVariants}>
        <div style={{ ...styles.hero, ...heroGradient }}>
          <div style={styles.heroInner}>
            <div style={styles.heroLeft}>
              <h1 style={styles.heroTitle}>
                <Users style={{ display: 'inline-block', width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' }} />
                                User Management
              </h1>
              <p style={styles.heroSub}>
                                Manage user accounts, roles, and permissions across your organization.
              </p>
            </div>

            <div style={styles.heroRight} className="hidden-md">
              <p style={styles.dateText}>
                {new Date().toLocaleDateString('en-IN', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p style={styles.metaText}>
                {user?.role ? `${user.role}` : '—'} {user?.department ? `• ${user.department}` : ''}
              </p>
              <div style={styles.actionButtons}>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Download style={{ width: 14, height: 14 }} />}
                  onClick={handleExport}
                  loading={isExporting}
                  disabled={isExporting}
                >
                  {isExporting ? 'Exporting...' : 'Export'}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Plus style={{ width: 14, height: 14 }} />}
                  onClick={handleAddUser}
                >
                                    Add User
                </Button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants}>
        <div style={{ background: '#fff', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(2,6,23,0.04)', border: '1px solid rgba(14,20,25,0.04)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {/* Search */}
            <div style={{ gridColumn: 'span 2' }}>
              <div style={{ position: 'relative' }}>
                <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)', width: 16, height: 16 }} />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ width: '100%', paddingLeft: '40px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid var(--gray-300)', borderRadius: '8px', outline: 'none' }}
                />
              </div>
            </div>

            {/* Role Filter */}
            <div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--gray-300)', borderRadius: '8px', outline: 'none' }}
              >
                <option value="all">All Roles</option>
                <option value="ADMIN">Admin</option>
                <option value="USER">User</option>
                <option value="CUSTOMER">Customer</option>
                <option value="SUPPLIER">Supplier</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--gray-300)', borderRadius: '8px', outline: 'none' }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      </motion.div>

      {/* User List */}
      <motion.div variants={itemVariants}>
        <div style={styles.cardWrapper}>
          <UserList
            searchQuery={searchQuery}
            statusFilter={statusFilter}
            roleFilter={roleFilter}
            refreshKey={refreshKey}
          />
        </div>
      </motion.div>

      {/* Add User Modal */}
      <UserEntryModal
        isOpen={showAddUserModal}
        onClose={handleCloseAddUserModal}
        mode="create"
      />
    </motion.div>
  );
};

export default UsersPage;